import { createFileRoute } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { FloatingLeftButton } from "@/components/FloatingLeftButton";
import { useNavigate } from "@tanstack/react-router";
import { DefaultButton } from "@/components/DefaultButton";
import { InputField } from "@/components/InputField";
import { PillButton } from "@/components/PillButton";
import { queryClient, useReferral, useStats } from "@/lib/hooks/useApi";
import { usePrivy } from "@privy-io/react-auth";
import { CircleX } from "lucide-react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTrigger,
} from "@/components/ui/dialog";
import { SheetCloseIcon } from "@/components/ui/sheet";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import configs from "@/lib/configs";
import { t } from "@/lib/i18n";
import AnimatedWaiting from "@/components/AnimatedWaiting";

export const Route = createFileRoute("/(game)/referrer/")({
  component: ReferrerPage,
});

const isRefCodeRequired = configs.IS_REFCODE_REQUIRED;

function ReferrerPage() {
  const navigate = useNavigate();
  const { user } = usePrivy();
  const [referralCode, setReferralCode] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [isSkipping, setIsSkipping] = useState(false);
  const [skipModalOpen, setSkipModalOpen] = useState(false);

  const { mutateAsync: addReferral } = useReferral();
  const { refetch: refetchStats } = useStats(user?.wallet?.address ?? "");

  useEffect(() => {
    const referralCode = localStorage.getItem("referralCode");
    if (user?.wallet?.address && referralCode) {
      setReferralCode(referralCode);
    }
  }, [addReferral, user?.wallet?.address]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handlePasteCode = () => {
    navigator.clipboard.readText().then((text) => {
      setReferralCode(text.trim());
      // setPasteVisible(false);
    });
  };

  const handleClearCode = () => {
    setReferralCode("");
  };

  const extractErrorMessage = (error: unknown): string => {
    const fallback = t("referrer.errors.default");
    const pickString = (value: unknown): string | undefined =>
      typeof value === "string" && value.trim() ? value : undefined;
    const withCode = (message: string, code: unknown): string => {
      const codeStr = pickString(code);
      return codeStr ? `[${codeStr}] ${message}` : message;
    };

    if (typeof error === "object" && error !== null) {
      if ("response" in error) {
        const data = (error as { response?: { data?: unknown } }).response
          ?.data as
          | {
              error?: unknown;
              message?: unknown;
              hint?: unknown;
              code?: unknown;
            }
          | undefined;
        const message =
          pickString(data?.error) ??
          pickString(data?.message) ??
          pickString(data?.hint) ??
          fallback;
        return withCode(message, data?.code);
      }
      if ("message" in error) {
        const message =
          pickString((error as { message?: unknown }).message) ?? fallback;
        return withCode(message, (error as { code?: unknown }).code);
      }
    }
    if (error instanceof Error) {
      return withCode(
        pickString(error.message) ?? fallback,
        (error as Error & { code?: unknown }).code,
      );
    }
    return fallback;
  };

  const handleSubmit = async () => {
    const codeToSend = referralCode.trim() ? referralCode : "";

    setIsSubmitting(true);
    setErrorMsg("");
    try {
      await addReferral({ referralCode: codeToSend });
      await refetchStats();
      queryClient.invalidateQueries({ queryKey: ["referral"] });
      navigate({ to: "/home" });
    } catch (error: unknown) {
      setErrorMsg(extractErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkip = async () => {
    setIsSkipping(true);
    setErrorMsg("");
    try {
      await addReferral({ referralCode: "skip" });
      await refetchStats();
      await queryClient.invalidateQueries({ queryKey: ["referral"] });
      setSkipModalOpen(false);
      navigate({ to: "/home", replace: true });
    } catch (error: unknown) {
      setSkipModalOpen(false);
      setErrorMsg(extractErrorMessage(error));
    } finally {
      setIsSkipping(false);
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center bg-[#FEF2E6]">
      {/* Floating Back Button */}
      <FloatingLeftButton
        onClick={() => navigate({ to: "/home" })}
        className="absolute left-0 top-4 z-50"
        icon={
          <svg
            className="w-4 h-4"
            viewBox="0 0 9 8"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M3 1V0H2V1H1V2H0V3H1V4H2V5H3V4H6V7H3V8H7V7H8V6H9V3H8V2H7V1H3Z"
              fill="white"
            />
          </svg>
        }
      />

      <div className="flex-1 h-full w-full p-6 flex flex-col items-center justify-center">
        {/* Header */}
        <div className="h-fit text-center mt-24 mb-8 flex flex-col items-center justify-center">
          <h1 className="uppercase text-2xl font-bold text-gray-900 mb-2">
            {t("referrer.title")}
          </h1>
          <img
            src="https://cdn.navalabs.io/assets/varentino_jFPzPH.png"
            alt="Varentino"
            className="size-32 object-contain"
          />
        </div>

        {/* Form */}
        <div className="flex flex-col w-full h-full">
          <div className="flex-1">
            <label
              htmlFor="referralCode"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              {t("referrer.form.label")}
            </label>
            <div className="relative">
              <InputField
                id="referralCode"
                type="text"
                value={referralCode}
                onChange={(value) => {
                  const upperValue = value.toUpperCase();
                  setReferralCode(upperValue);
                  setErrorMsg("");
                }}
                placeholder={t("referrer.form.placeholder")}
                className="w-full pr-32"
                disabled={isSubmitting}
                showIcon={true}
                icon={
                  <div className="flex items-center gap-2">
                    {referralCode.trim() && (
                      <button
                        type="button"
                        onClick={handleClearCode}
                        className="p-1 text-gray-400 hover:text-gray-600"
                        disabled={isSubmitting}
                      >
                        <CircleX className="w-4 h-4" />
                      </button>
                    )}
                    {/* <PillButton */}
                    {/*   type="button" */}
                    {/*   onClick={handleSubmit} */}
                    {/*   variant={referralCode.trim() ? "green" : "white"} */}
                    {/*   disabled={isSubmitting || !referralCode.trim()} */}
                    {/*   className="h-8 px-3 text-xs" */}
                    {/* > */}
                    {/*   {t("referrer.form.submit")} */}
                    {/* </PillButton> */}
                    <button
                      onClick={handleSubmit}
                      disabled={isSubmitting || !referralCode.trim()}
                      className="green-elevated-container text-[12px] uppercase px-4! disabled:brightness-100 disabled:saturate-100 disabled:grayscale-75 disabled:cursor-not-allowed hover:cursor-pointer hover:brightness-95 hover:saturate-95"
                    >
                      {t("referrer.form.submit")}
                    </button>
                  </div>
                }
              />
            </div>
            {errorMsg && (
              <p className="mt-2 text-sm text-red-600">{errorMsg}</p>
            )}
          </div>

          <button
            onClick={handleSubmit}
            className="green-elevated-container h-12 uppercase hover:cursor-pointer hover:brightness-95 hover:saturate-95 active:scale-98 disabled:grayscale-50 disabled:brightness-100 disabled:saturate-100 disabled:cursor-not-allowed disabled:scale-100"
            disabled={isSubmitting || !referralCode.trim()}
          >
            {isSubmitting ? (
              <AnimatedWaiting prefix={t("referrer.form.submitting")} />
            ) : (
              t("referrer.form.submitButton")
            )}
          </button>

          {!isRefCodeRequired && (
            <Dialog open={skipModalOpen} onOpenChange={setSkipModalOpen}>
              <DialogTrigger asChild>
                <button className="gray-container cursor-pointer px-4 py-2 mt-2 mb-2 transition-all active:scale-98 hover:saturate-95 hover:brightness-95">
                  <span
                    className={cn(
                      "text-sm xs:text-base font-black uppercase text-white",
                    )}
                  >
                    {t("referrer.skip.button")}
                  </span>
                </button>
              </DialogTrigger>
              <DialogContent
                className="max-w-[364px]! rounded-2xl bg-[#254637] shadow-xl border-2 border-b-4 border-black w-full items-center p-4"
                style={{
                  boxShadow: "inset 0 3px 0 #395749, inset 0 -3px 0 #1A3C2D",
                }}
              >
                <div className="flex flex-col">
                  <DialogClose>
                    <SheetCloseIcon className="absolute top-2 right-2" />
                  </DialogClose>
                  <p className="text-sm font-extrabold text-white text-shadow-black-sm mr-3">
                    {t("referrer.skip.modal.benefit")}
                  </p>
                  <p className="text-sm font-extrabold text-white text-shadow-black-sm mt-2">
                    {t("referrer.skip.modal.warning")}{" "}
                    <span className="text-sm font-extrabold text-white text-shadow-black-sm mt-2">
                      {t("referrer.skip.modal.discordHint")}{" "}
                      <a
                        href="https://discord.gg/GQuP7ekfF9"
                        className="text-emerald-200"
                      >
                        {t("referrer.skip.modal.discord")}
                      </a>{" "}
                    </span>
                  </p>

                  <span className="text-sm font-extrabold text-white text-shadow-black-sm mt-2">
                    {t("referrer.skip.modal.confirmQuestion")}
                  </span>

                  <DefaultButton
                    variant="green"
                    disabled={isSkipping}
                    onClick={handleSkip}
                    className="h-10.5  mt-4"
                  >
                    {isSkipping ? (
                      <Spinner className="w-4 h-4" />
                    ) : (
                      <>
                        <span className="text-white font-bold clamp-[text,sm,base] -mt-1 text-shadow-red">
                          {t("referrer.skip.modal.confirmButton")}
                        </span>
                      </>
                    )}
                  </DefaultButton>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>
    </div>
  );
}
